import "dotenv/config";
import Chart from "chart.js/auto";

import {
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

const trendSymbols = {
  SingleDown: "↓",
  FortyFiveDown: "↘",
  Flat: "→",
  FortyFiveUp: "↗",
  SingleUp: "↑",
  NotComputable: "",
};

const header = document.getElementById("header");
const currentValue = document.getElementById("currentValue");
const currentDate = document.getElementById("currentDate");
const statusMessage = document.getElementById("statusMessage");
const unit = document.getElementById("unit");
let countdownTimer = 70;
let statusEmoji = "🟢";

const ctx = document.getElementById("historyChart").getContext("2d");
let chart;

const audio = document.getElementById("messageSound");
const toggleBtn = document.getElementById("audioToggle");
let soundEnabled = false;

toggleBtn.addEventListener("click", () => {
  soundEnabled = !soundEnabled;
  toggleBtn.textContent = soundEnabled ? "🔊 On" : "🔇 Off";
  // Play silent sound once to unlock audio context on first click
  if (soundEnabled) {
    audio.play().catch(() => {});
    audio.pause();
    audio.currentTime = 0;
  }
});

async function fetchLibreData() {
  let data;
  try {
    const res = await fetch(process.env.PARCEL_BACKEND_URL);
    data = await res.json();
  } catch (err) {}

  if (!data.error) {
    updateTimer(data);
    updateCurrentValue(data);
    updateHistoryChart([...data.history, data.current]);
  }
}

function updateTimer(data) {
  clearInterval(countdownTimer);

  let secondsLeft = data.retryAfter;
  statusMessage.textContent = `Next update begins in ${secondsLeft} seconds.`;

  countdownTimer = setInterval(() => {
    secondsLeft--;
    if (secondsLeft > 0) {
      statusMessage.textContent = `Next update begins in ${secondsLeft} seconds.`;
    } else {
      statusMessage.textContent = `Update in progress...`;
      clearInterval(countdownTimer);
    }
  }, 1000);
}

function updateCurrentValue(data) {
  updateCriticalStatus(data);

  if (data.current.value) {
    const arrow = trendSymbols[data.current.trend] || "";
    const dateObj = new Date(data.current.date);
    dateObj.setSeconds(0, 0);
    const date = dateObj.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const now = new Date();
    now.setSeconds(0, 0);
    const delayMinutes = Math.max(
      0,
      Math.floor((now.getTime() - dateObj.getTime()) / 60000),
    );
    const timePrefix = `🕒 ${date}`;
    const delaySuffix = delayMinutes > 0 ? ` ⏳ ${delayMinutes}'` : "";
    const displayTime = `${timePrefix}${delaySuffix}`;

    document.title = `${statusEmoji} ${data.current.value} ${arrow} ${displayTime}`;
    currentValue.textContent = `${data.current.value}${arrow}`;
    currentDate.textContent = displayTime;
    unit.textContent = ""; // `mg/dL`;
  } else {
    currentValue.textContent = `---`;
  }
}

function updateCriticalStatus(data) {
  const value = data.current.value;

  if (value < 70 || value > 240) {
    header.style.backgroundColor = "rgba(255, 0, 0, 0.6)";
    statusEmoji = "🔴"; // Critical
  } else if ((value >= 180 && value <= 240) || (value >= 70 && value <= 80)) {
    header.style.backgroundColor = "rgba(255, 165, 0, 0.6)";
    statusEmoji = "🟠"; // Warning
  } else if (value > 80 && value < 180) {
    header.style.backgroundColor = "rgba(0, 128, 0, 0.6)";
    statusEmoji = "🟢"; // Normal
  }

  if (value < 100 || value > 240) {
    if (soundEnabled) {
      audio.currentTime = 0;
      audio.play().catch((e) => console.error("Error playing sound:", e));
    }
  }
}

function updateHistoryChart(data) {
  const labels = data.map((d) =>
    new Date(d.date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  );
  const values = data.map((d) => d.value);

  if (!chart) {
    chart = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "",
            data: values,
            borderColor: "white",
            backgroundColor: "rgba(0,0,255,0.0)",
          },
        ],
      },
      options: {
        responsive: true,
        interaction: {
          mode: "nearest",
          intersect: true,
        },
        elements: {
          point: {
            radius: 2,
            hoverRadius: 4,
            backgroundColor: "blue",
            hoverBackgroundColor: "yellow",
          },
        },
        scales: {
          x: { title: { display: true, text: "Time" } },
          y: { title: { display: true, text: "mg/dL" } },
        },
        plugins: {
          tooltip: { enabled: true },
          annotation: {
            annotations: {
              thresholdLine100: {
                type: "line",
                yMin: 100,
                yMax: 100,
                borderColor: "red",
                borderWidth: 2,
                borderDash: [5, 5], // dotted line
                label: {
                  enabled: true,
                  content: "Threshold 100",
                  position: "end",
                },
              },
              thresholdLine240: {
                type: "line",
                yMin: 240,
                yMax: 240,
                borderColor: "red",
                borderWidth: 2,
                borderDash: [5, 5], // dotted line
                label: {
                  enabled: true,
                  content: "Threshold 100",
                  position: "end",
                },
              },
              greenBox: {
                type: "box",
                yMin: 70,
                yMax: 180,
                backgroundColor: "rgba(0, 255, 0, 0.2)",
                borderColor: "green",
                borderWidth: 1,
                drawTime: "beforeDatasetsDraw",
              },
              // hoverLine: {
              //   type: "line",
              //   xMin: labels[labels.length - 1],
              //   xMax: labels[labels.length - 1],
              //   borderColor: "white",
              //   borderWidth: 1,
              // },
            },
          },
        },
        // onHover: (event, elements) => {
        //   if (elements.length) {
        //     const index = elements[0].index;
        //     const xValue = chart.data.labels[index];
        //     chart.options.plugins.annotation.annotations.hoverLine.xMin =
        //       xValue;
        //     chart.options.plugins.annotation.annotations.hoverLine.xMax =
        //       xValue;
        //   } else {
        //     // chart.options.plugins.annotation.annotations.hoverLine.xMin = 0;
        //     // chart.options.plugins.annotation.annotations.hoverLine.xMax = 0;
        //   }
        //   chart.update("none");
        // },
      },
    });
  } else {
    chart.data.labels = labels;
    chart.data.datasets[0].data = values;
    chart.update();
  }
}

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(new URL("./sw.js", import.meta.url))
      .catch((error) => {
        console.error("Service worker registration failed:", error);
      });
  });
}

fetchLibreData();
setInterval(fetchLibreData, 10_000);
